import { action } from "typesafe-actions";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { AnyAction } from "redux";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { api } from "../../api/api";
import {
  IFoodItem,
  IMasterFoodMenu,
  IFoodMenuCategory,
  ILeadFoodMenu,
} from "./food.types";
import { ISearchQueryParams } from "../store.types";
import { getSearchQueryFromParams } from "../store.utils";
import {
  ILeadFoodMenuApiResponse,
  ILeadFoodMenuPreviewData,
} from "../../views/leads/LeadFoodMenu/LeadFoodMenu.types";
import { isArray } from "lodash";
import { getUniqueId } from "../../helpers";
import { getFileNameFromUrl, uploadMultipleFile } from "../../components/FileUpload/utils";
import { IFileUpload } from "../../components/FileUpload/FileUpload.type";

export const FETCH_FOOD_ITEMS_LIST_PROGRESS = "FETCH_FOOD_ITEMS_LIST_PROGRESS";
export const FETCH_FOOD_ITEMS_LIST_SUCCESS = "FETCH_FOOD_ITEMS_LIST_SUCCESS";
export const FETCH_FOOD_ITEMS_LIST_FAILED = "FETCH_FOOD_ITEMS_LIST_FAILED";

export const fetchFoodItemsListProgress = () =>
  action(FETCH_FOOD_ITEMS_LIST_PROGRESS);
export const fetchFoodItemsListSuccess = (
  data: IFoodItem[],
  totalRecords: number,
) => action(FETCH_FOOD_ITEMS_LIST_SUCCESS, { data, totalRecords });
export const fetchFoodItemsListFailed = () =>
  action(FETCH_FOOD_ITEMS_LIST_FAILED);

export const fetchFoodItemsListAsync =
  (
    companyOutletUuid: string,
    pageNumber: number,
    itemPerPage: number,
    pageLimit: number,
    status: string,
    date: IDataTableV2DateState["dates"],
    searchValue?: IDatatableV2AdvancedSearchFilter,
    searchByFoodItem?: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      dispatch(fetchFoodItemsListProgress());
      try {
        dispatch(saveLoaderProgress());
        let searchQuery = "";

        if (searchByFoodItem) {
          searchQuery += `&item_name=${encodeURIComponent(searchByFoodItem)}`;
        }

        if (searchValue?.length ?? 0) {
          const advanceFilter = [...(searchValue || [])];
          searchQuery += `&advanceFilter=${JSON.stringify(advanceFilter)}`;
        }

        searchQuery += `&from_date=${date.fromDate}&to_date=${date.toDate}`;

        if (status && status !== "-1") {
          searchQuery += `&status=${status}`;
        }

        const response = await api.get(
          `/food/get-food-item?company_outlet_uuid=${companyOutletUuid}&pageNo=${pageNumber}&pageLimit=${pageLimit}&itemPerPage=${itemPerPage}` +
          searchQuery,
        );
        const data: IFoodItem[] = response.data.data;

        const totalRecords = response.data.totalRecords;
        dispatch(fetchFoodItemsListSuccess(data, totalRecords));
      } catch (error: any) {
        dispatch(fetchFoodItemsListFailed());
        dispatch(
          showMessage({
            type: "error",
            message: error.response.data.message,
            displayAs: "snackbar",
          }),
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };

export const upsertFoodItemBulk =
  (
    data: IFoodItem[],
    onCallback?: (isSuccess: boolean, data?: IFoodItem[]) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      dispatch(saveLoaderProgress());
      try {
        const response = await api.post("/food/upsert-food-item", data);
        dispatch(saveLoaderCompleted());

        dispatch(
          showMessage({
            type: "success",
            message: "Categories saved successfully",
            displayAs: "snackbar",
          }),
        );

        onCallback?.(true, response.data);
        return response.data;
      } catch (error: any) {
        dispatch(saveLoaderCompleted());
        onCallback?.(false);

        dispatch(
          showMessage({
            type: "error",
            message:
              error.response?.data?.message || "Failed to save food categories",
            displayAs: "snackbar",
          }),
        );
        throw error;
      }
    };

//food menus actions
export const FETCH_FOOD_MENUS_LIST_PROGRESS = "FETCH_FOOD_MENUS_LIST_PROGRESS";
export const FETCH_FOOD_MENUS_LIST_SUCCESS = "FETCH_FOOD_MENUS_LIST_SUCCESS";
export const FETCH_FOOD_MENUS_LIST_FAILED = "FETCH_FOOD_MENUS_LIST_FAILED";

export const fetchFoodMenusListProgress = () =>
  action(FETCH_FOOD_MENUS_LIST_PROGRESS);
export const fetchFoodMenusListSuccess = (
  data: IMasterFoodMenu[],
  totalRecords: number,
) => action(FETCH_FOOD_MENUS_LIST_SUCCESS, { data, totalRecords });
export const fetchFoodMenusListFailed = () =>
  action(FETCH_FOOD_MENUS_LIST_FAILED);

export const fetchMasterFoodMenusListAsync =
  (params: ISearchQueryParams): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      dispatch(fetchFoodMenusListProgress());
      try {
        dispatch(saveLoaderProgress());
        const query = getSearchQueryFromParams(params);
        const response = await api.get(`/food/get-master-menu${query}`);
        const rawData: any[] = response.data.data || [];
        const data: IMasterFoodMenu[] = rawData.map((menu: any) => ({
          ...menu,
          attachments: Array.isArray(menu.attachments)
            ? menu.attachments
              .filter((item: any) => typeof item === "string")
              .map((file: string) => ({
                key: getUniqueId(),
                type: file.includes("https://images.unsplash.com/") ? "STATIC" : "SERVER",
                file: null,
                name: getFileNameFromUrl(file || ""),
                path: file,
              } as IFileUpload))
            : [],
        }));
        const totalRecords = response.data.totalRecords;
        dispatch(fetchFoodMenusListSuccess(data, totalRecords));
      } catch (error) {
        dispatch(fetchFoodMenusListFailed());
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };

// Single master food menu actions
export const FETCH_SINGLE_FOOD_MENU_PROGRESS = "FETCH_SINGLE_FOOD_MENU_PROGRESS";
export const FETCH_SINGLE_FOOD_MENU_SUCCESS = "FETCH_SINGLE_FOOD_MENU_SUCCESS";
export const FETCH_SINGLE_FOOD_MENU_FAILED = "FETCH_SINGLE_FOOD_MENU_FAILED";

export const fetchSingleFoodMenuProgress = () =>
  action(FETCH_SINGLE_FOOD_MENU_PROGRESS);
export const fetchSingleFoodMenuSuccess = (data: IMasterFoodMenu) =>
  action(FETCH_SINGLE_FOOD_MENU_SUCCESS, { data });
export const fetchSingleFoodMenuFailed = () =>
  action(FETCH_SINGLE_FOOD_MENU_FAILED);

export const fetchSingleMasterFoodMenuAsync =
  (
    masterMenuUuid: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      dispatch(fetchSingleFoodMenuProgress());
      try {
        dispatch(saveLoaderProgress());
        const response = await api.get(
          `/food/get-master-menu?master_menu_uuid=${masterMenuUuid}`,
        );
        const data = response.data.data;
        if (data && data.length > 0) {
          let foodMenuData = data[0];
          foodMenuData.attachments = isArray(foodMenuData.attachments)
            ? foodMenuData.attachments
              .filter((item: any) => typeof item === "string")
              .map((file: string) => {
                return {
                  key: getUniqueId(),
                  type: file.includes("https://images.unsplash.com/") ? "STATIC" : "SERVER",
                  file: null,
                  name: getFileNameFromUrl(file || ""),
                  path: file,
                } as IFileUpload;
              })
            : [];

          dispatch(fetchSingleFoodMenuSuccess(data[0]));
        } else {
          dispatch(fetchSingleFoodMenuFailed());
        }
      } catch (error: any) {
        dispatch(fetchSingleFoodMenuFailed());
        dispatch(
          showMessage({
            type: "error",
            message: error.response?.data?.message || "Failed to fetch menu",
            displayAs: "snackbar",
          }),
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };

export const upsertMasterFoodMenu =
  (
    data: IMasterFoodMenu,
    onCallback?: (isSuccess: boolean, data?: IMasterFoodMenu) => void,
  ) =>
    async (dispatch: any) => {
      try {
        let payload = data
        const asPayload = {
          title: payload.title,
        };
        // Corrected logic for mapping receipts
        const fileToUpload = isArray(payload.attachments)
          ? payload.attachments.filter((item) => item.type === "LOCAL")
          : [];
        if (fileToUpload.length > 0) {
          const { paths } = await uploadMultipleFile(
            fileToUpload,
            "LEADS",
            asPayload,
          );
          payload.attachments = payload.attachments
            .filter((item) => item.type && ["SERVER", "STATIC"].includes(item.type) && item.path)
            .map((item) => item.path!)
            .concat(paths as string[]) as any;
        } else {
          payload.attachments = payload.attachments
            .filter((item) => item.type && ["SERVER", "STATIC"].includes(item.type) && item.path)
            .map((item) => item.path!) as any;
        }

        const response = await api.post("/food/upsert-master-menu", payload);

        dispatch(
          showMessage({
            type: "success",
            message: `${data.master_menu_uuid
              ? "Master Food menu updated successfully"
              : "Master Food menu created successfully"
              }`,
            displayAs: "snackbar",
          }),
        );
        onCallback?.(true, response.data);
        return response.data;
      } catch (error: any) {
        dispatch(
          showMessage({
            type: "error",
            message: error.response.data.message,
            displayAs: "snackbar",
          }),
        );
        onCallback?.(false);
      }
    };

// Lead  menu actions

export const FETCH_LEAD_FOOD_MENUS_LIST_PROGRESS =
  "FETCH_LEAD_FOOD_MENUS_LIST_PROGRESS";
export const FETCH_LEAD_FOOD_MENUS_LIST_SUCCESS =
  "FETCH_LEAD_FOOD_MENUS_LIST_SUCCESS";
export const FETCH_LEAD_FOOD_MENUS_LIST_FAILED =
  "FETCH_LEAD_FOOD_MENUS_LIST_FAILED";

export const fetchLeadFoodMenusListProgress = () =>
  action(FETCH_LEAD_FOOD_MENUS_LIST_PROGRESS);
export const fetchLeadFoodMenusListSuccess = (
  data: ILeadFoodMenu[],
  totalRecords: number,
) => action(FETCH_LEAD_FOOD_MENUS_LIST_SUCCESS, { data, totalRecords });
export const fetchLeadFoodMenusListFailed = () =>
  action(FETCH_LEAD_FOOD_MENUS_LIST_FAILED);

export const fetchLeadFoodMenusListAsync =
  (
    leads_uuid: string,
    company_outlet_uuid: string,
    added_lead_menu_uuid?: string | null,
    pageNo?: number,
    itemPerPage?: number,
    pageLimit?: number,

    advanceFilter?: IDatatableV2AdvancedSearchFilter,
    onCallback?: (isSuccess: boolean, data?: ILeadFoodMenu[]) => void,
  ) =>
    async (dispatch: any) => {
      dispatch(fetchLeadFoodMenusListProgress());
      try {
        let searchQuery = "";

        if (added_lead_menu_uuid) {
          searchQuery += `&added_lead_menu_uuid=${added_lead_menu_uuid}`;
        }

        if (pageNo) {
          searchQuery += `&pageNo=${pageNo}`;
        }

        if (itemPerPage) {
          searchQuery += `&itemPerPage=${itemPerPage}`;
        }
        if (pageLimit) {
          searchQuery += `&pageLimit=${pageLimit}`;
        }

        const response = await api.get(
          `/food/get-lead-menu?leads_uuid=${leads_uuid}&company_outlet_uuid=${company_outlet_uuid}` +
          searchQuery,
        );
        const data: ILeadFoodMenu[] = response.data.data;
        const totalRecords = response.data.totalRecords;
        dispatch(fetchLeadFoodMenusListSuccess(data, totalRecords));
        onCallback?.(true, data);
      } catch (error) {
        onCallback?.(false);
        dispatch(fetchLeadFoodMenusListFailed());
      }
    };

export const upsertLeadFoodMenu =
  (
    data: ILeadFoodMenu,
    onCallback?: (isSuccess: boolean, data?: ILeadFoodMenu) => void,
  ) =>
    async (dispatch: any) => {
      try {
        const response = await api.post("/food/upsert-lead-menu", data);

        dispatch(saveLoaderCompleted());
        dispatch(
          showMessage({
            type: "success",
            message: `${data.added_lead_menu_uuid === null
              ? "Lead Food menu Created successfully"
              : "Lead Food menu Saved successfully"
              }`,
            displayAs: "snackbar",
          }),
        );

        // return response.data;
        onCallback?.(true, response.data);
      } catch (error: any) {
        onCallback?.(false);
        dispatch(fetchLeadFoodMenusListFailed());
        dispatch(
          showMessage({
            type: "error",
            message: error.response.data.message,
            displayAs: "snackbar",
          }),
        );
      }
    };

// categories actions

export const FETCH_CATEGORY_LIST_PROGRESS = "FETCH_CATEGORY_LIST_PROGRESS";
export const FETCH_CATEGORY_LIST_SUCCESS = "FETCH_CATEGORY_LIST_SUCCESS";
export const FETCH_CATEGORY_LIST_FAILED = "FETCH_CATEGORY_LIST_FAILED";

export const fetchCategoryListProgress = () =>
  action(FETCH_CATEGORY_LIST_PROGRESS);
export const fetchCategoryListSuccess = (
  data: IFoodMenuCategory[],
  currentRecords: number,
) => action(FETCH_CATEGORY_LIST_SUCCESS, { data, currentRecords });
export const fetchCategoryListFailed = () => action(FETCH_CATEGORY_LIST_FAILED);

export const fetchFoodMenuCategoryListAsync =
  (
    companyOutletUuid: string,
    pageNumber: number,
    itemPerPage: number,
    pageLimit: number,
    status: string,
    date: IDataTableV2DateState["dates"],
    category_uuid?: string | null,
    searchValue?: IDatatableV2AdvancedSearchFilter,
    searchByName?: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      dispatch(fetchCategoryListProgress());
      try {
        dispatch(saveLoaderProgress());
        let searchQuery = "";

        if (searchByName) {
          searchQuery += `&category_name=${searchByName}`;
        }
        if (searchValue?.length ?? 0) {
          const advanceFilter = [...(searchValue || [])];
          searchQuery += `&advanceFilter=${JSON.stringify(advanceFilter)}`;
        }
        searchQuery += `&from_date=${date.fromDate}&to_date=${date.toDate}`;

        if (category_uuid) {
          searchQuery += `&category_uuid=${category_uuid}`;
        }

        if (status !== "-1") {
          searchQuery += `&status=${status}`;
        }

        const response = await api.get(
          `/food/get-category?company_outlet_uuid=${companyOutletUuid}&pageNo=${pageNumber}&pageLimit=${pageLimit}&itemPerPage=${itemPerPage}` +
          searchQuery,
        );

        dispatch(
          fetchCategoryListSuccess(
            response.data.data,
            response.data.currentRecords,
          ),
        );
      } catch (error) {
        dispatch(fetchCategoryListFailed());
        dispatch(
          showMessage({
            type: "error",
            message: "Failed to fetch categories",
            displayAs: "snackbar",
          }),
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };

export const upsertFoodMenuCategoryBulk =
  (
    data: IFoodMenuCategory[],
    onCallback?: (isSuccess: boolean, data?: IFoodMenuCategory[]) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      dispatch(saveLoaderProgress());
      try {
        const response = await api.post("/food/upsert-category", data); // 👈 ARRAY
        dispatch(saveLoaderCompleted());

        dispatch(
          showMessage({
            type: "success",
            message: "Categories saved successfully",
            displayAs: "snackbar",
          }),
        );

        onCallback?.(true, response.data);
        return response.data;
      } catch (error: any) {
        dispatch(saveLoaderCompleted());
        onCallback?.(false);

        dispatch(
          showMessage({
            type: "error",
            message:
              error.response?.data?.message || "Failed to save food categories",
            displayAs: "snackbar",
          }),
        );
        throw error;
      }
    };

export const fetchFoodMenuCategoryListWithFoodItemsAsync =
  (params: ISearchQueryParams): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      dispatch(fetchCategoryListProgress());
      try {
        dispatch(saveLoaderProgress());
        const query = getSearchQueryFromParams(params);

        const response = await api.get(
          `/food/get-category-with-food-items${query}`,
        );

        dispatch(
          fetchCategoryListSuccess(
            response.data.data,
            response.data.currentRecords,
          ),
        );
      } catch (error) {
        dispatch(fetchCategoryListFailed());
        dispatch(
          showMessage({
            type: "error",
            message: "Failed to fetch categories",
            displayAs: "snackbar",
          }),
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };

export const previewAndDownloadLeadFoodMenuAsync =
  (
    experience_booking_uuid: string,
    previewOnly: boolean,
    onCallback: (isSuccess: boolean, data?: ILeadFoodMenuPreviewData) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      try {
        const res = await api.get<ILeadFoodMenuApiResponse>(
          `/food/lead-menu/${experience_booking_uuid}/PREVIEW?multiple_events=1`,
          previewOnly
            ? {}
            : {
              responseType: "arraybuffer",
            },
        );

        if (!previewOnly) {
          const blob = new Blob([res.data as any], {
            type: "application/pdf",
          });

          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "Lead-Food-Menu.pdf");
          document.body.appendChild(link);
          link.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(link);

          onCallback(true);
        } else {
          onCallback(true, res.data.data[0]); // ✅ correctly typed now
        }
      } catch (err: any) {
        onCallback(false);
      }
    };

export const CLEAR_FOOD_STATE = "CLEAR_FOOD_STATE";
export const clearFoodState = () => action(CLEAR_FOOD_STATE);

export const CLEAR_LEAD_FOOD_MENU_STATE = "CLEAR_LEAD_FOOD_MENU_STATE";
export const clearLeadFoodMenuState = () => action(CLEAR_LEAD_FOOD_MENU_STATE);
